import CalendarItemNotificationPayload from 'owa-service/lib/contract/CalendarItemNotificationPayload';
import SubscriptionParameters from 'owa-service/lib/contract/SubscriptionParameters';
import { trace } from 'owa-trace';
import {
    lazyGetChannelId,
    lazySubscribe,
    lazyUnsubscribe,
    NotificationSubscription,
} from 'owa-notification';

/**
 * Subscription data for a given calendar
 */
interface SubscriptionData {
    folderId: string;
    subscription: NotificationSubscription;
}

export default class CalendarItemNotificationManager {
    // Subscriptions map with key = folderId and value = SubscriptionData for that calendar folder
    private subscriptions: { [folderId: string]: SubscriptionData } = {};
    private handler: (folderId: string) => (notification: CalendarItemNotificationPayload) => void;

    constructor(
        handler: (folderId: string) => (notification: CalendarItemNotificationPayload) => void
    ) {
        this.subscriptions = {};
        this.handler = handler;
    }

    private getSubscription(folderId: string) {
        return this.subscriptions[folderId];
    }

    private isSubscribed(folderId: string) {
        return !!this.getSubscription(folderId);
    }

    private setSubscription(folderId: string, subscriptionData: SubscriptionData) {
        this.subscriptions[folderId] = subscriptionData;
    }

    private deleteSubscription(folderId: string) {
        delete this.subscriptions[folderId];
    }

    subscribeNotifications(folderId: string) {
        if (this.isSubscribed(folderId)) {
            // We are already subscribed to this folder, log it and exit
            trace.info(
                `[CalendarNotifications] Already subscribed to notifications on folder: ${folderId}`
            );
            return;
        }

        lazyGetChannelId.import().then(getChannelId => {
            const subscriptionParameters: SubscriptionParameters = {
                ChannelId: getChannelId(),
                NotificationType: 'CalendarItemNotification',
                FolderId: folderId,
            };

            const subscription: NotificationSubscription = {
                subscriptionId: `${subscriptionParameters.NotificationType}${folderId}`,
                requiresExplicitSubscribe: true,
                subscriptionParameters: subscriptionParameters,
            };

            const subscriptionData: SubscriptionData = {
                folderId: folderId,
                subscription: subscription,
            };

            trace.info(
                `[CalendarNotifications] Subscribing calendar item notifications. Folder: ${folderId}`
            );
            lazySubscribe.importAndExecute(subscription, this.handler(folderId));

            this.setSubscription(folderId, subscriptionData);
        });
    }

    unsubscribeNotifications(folderId: string) {
        const subscriptionData = this.getSubscription(folderId);

        if (subscriptionData) {
            trace.info(
                `[CalendarNotifications] Unsubscribing calendar item notifications. Folder: ${folderId}`
            );
            lazyUnsubscribe.importAndExecute(subscriptionData.subscription, this.handler);
            this.deleteSubscription(folderId);
        } else {
            trace.warn(
                `[CalendarNotifications] Could not unsubscribe notifications on folder: '${folderId}', subscription data not found`
            );
        }
    }
}
